import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { App } from 'antd';
import { useInboundPackageByIdQuery, useLazyGetDeliveryNoteQuery } from 'api/warehouse';
import { WAREHOUSE_ROUTES } from 'constants/routes';

export const useLabels = () => {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [packages, setPackages] = useState<any[]>([]);

  const [getDeliveryNote, { isLoading: isDownloadingNote }] = useLazyGetDeliveryNoteQuery();

  const packagesFromState = location.state as { packages?: any[] } | undefined;

  const {
    data: order,
    isLoading,
    error,
  } = useInboundPackageByIdQuery(id || '', {
    skip: !id,
  });

  useEffect(() => {
    if (error) {
      message.error('Failed to load order details');
      navigate(WAREHOUSE_ROUTES.inboundPackages);
    }
  }, [error, navigate, message]);

  useEffect(() => {
    if (!packagesFromState?.packages || packagesFromState.packages.length === 0) {
      message.warning('No packages to display');
      navigate(`/warehouse/inbound/${id}`);
    } else {
      setPackages(packagesFromState.packages);
    }
  }, [packagesFromState, id, navigate, message]);

  const handlePrint = useCallback(
    (packageId: string) => {
      // TODO: Implement print functionality for specific package
      message.info('Print functionality coming soon');
      console.log('Print package:', packageId);
    },
    [message]
  );

  const handlePrintDeliveryNote = useCallback(
    async (packageId: string) => {
      if (!packageId) {
        message.error('No package ID available');
        return;
      }

      try {
        const response = await getDeliveryNote(packageId).unwrap();

        // Extract the ATTACHMENTS URL from the response
        const attachmentUrl = response?.data?.files?.ATTACHMENTS;

        if (!attachmentUrl) {
          message.error('Delivery note URL not found');
          return;
        }

        // Open the URL - browser will handle it (download or open)
        window.location.href = attachmentUrl;

        message.success('Delivery note download started');
      } catch (error: any) {
        message.error(error?.message || 'Failed to load delivery note');
      }
    },
    [getDeliveryNote, message]
  );

  const handleComplete = useCallback(() => {
    message.success('All labels processed successfully');
    navigate(`/warehouse/inbound/${id}`);
  }, [id, navigate, message]);

  const completedCount =
    order?.itemsResponseList?.filter((item: any) => item.status === 'CHECKED' || item.status === 'CLAIMED').length || 0;

  const totalCount = order?.itemsResponseList?.length || 0;

  return {
    isLoading,
    packages,
    isDownloadingNote,
    handlePrint,
    handlePrintDeliveryNote,
    handleComplete,
    completedCount,
    totalCount,
  };
};
